/**
 * THE BOTTOM NAV, from the artboards in `faithfully-kept-business/design/home/`: a navy bar of
 * tabs, each an icon over its name, 72px tall, the place shown marked with a gold rule along its top
 * and white words, the others in the muted light blue. It sits at the foot of the screen while the
 * page scrolls, so the other places are one tap away wherever the thumb is. The bar runs the whole
 * width, and its tabs are in the page's one column (`column.ts`), so at a desk they sit under the
 * page and not spread across the window.
 *
 * Home, Schedule, Payments and Messages, in the order drawn. Payments is a client's alone (the
 * seam, section 4: a member is "never money"), so it is drawn only where the place shown says she
 * pays here (`payments`), which the Worker decides, and on Payments itself, which nobody else
 * reaches. Everybody else is offered the other three, so no tab leads to a place that would turn
 * her away.
 *
 * Each icon is hidden from assistive technology, so a tab is read once, by its name, and the place
 * shown carries `aria-current="page"`. On Account and its pages no tab is the place shown: the
 * header's Account button carries it instead (slice A1), and the marks stay. The focus ring is the
 * light one, because the page's dark ring would vanish on the navy.
 *
 * THE MARKS (provisional P49), which no artboard draws, so business issue 13 carries them: on
 * Payments, how many invoices she can pay from home now; on Messages, how many things the office has
 * said since she last opened it, but on Messages itself, where she is reading them; and on Home, a
 * mark while a clean of hers waits for "How was it?". A count is its number in a small badge at the
 * icon's shoulder, "9+" past nine, in the bar's light gold with navy figures; Home's mark is a gold
 * dot ringed in navy. Each is hidden from assistive technology and said once, in words, in the
 * name the tab is read by, its own name first ("Payments, 2 to pay"), so the visible name is in the
 * name read and no mark is a number, a dot or a colour alone. Nothing is marked with nothing to
 * mark, and the tab is then read by its name alone, as drawn.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { countInWords, countShown, type NavMarks } from '../lib/marks.ts';
import { HOME_PATH, MESSAGES_PATH, PAYMENTS_PATH, SCHEDULE_PATH } from '../paths.ts';
import { NAV, NAV_MARKS } from '../words.ts';
import { COLUMN } from './column.ts';

export type Tab = 'home' | 'schedule' | 'payments' | 'messages';

/** The artboards' icons, 24px, drawn in the tab's own colour. */
function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const TABS: readonly { readonly tab: Tab; readonly to: string; readonly icon: ReactNode }[] = [
  {
    tab: 'home',
    to: HOME_PATH,
    icon: (
      <Icon>
        <path d="M3 11.5 12 4l9 7.5" />
        <path d="M5.5 10v9.5h13V10" />
        <path d="M10 19.5v-5h4v5" />
      </Icon>
    ),
  },
  {
    tab: 'schedule',
    to: SCHEDULE_PATH,
    icon: (
      <Icon>
        <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
        <path d="M3.5 9.5h17M8 3v4M16 3v4" />
      </Icon>
    ),
  },
  {
    tab: 'payments',
    to: PAYMENTS_PATH,
    icon: (
      <Icon>
        <rect x="2.5" y="5.5" width="19" height="13" rx="2" />
        <path d="M2.5 10h19M6.5 14.5h4" />
      </Icon>
    ),
  },
  {
    tab: 'messages',
    to: MESSAGES_PATH,
    icon: (
      <Icon>
        <path d="M4.5 5h15a1.5 1.5 0 0 1 1.5 1.5V15a1.5 1.5 0 0 1-1.5 1.5H10l-4 3.5v-3.5H4.5A1.5 1.5 0 0 1 3 15V6.5A1.5 1.5 0 0 1 4.5 5z" />
      </Icon>
    ),
  },
];

/**
 * The ring is important because the page's own ring in `styles.css` sits outside Tailwind's layers,
 * and would otherwise win; it is drawn inside the tab, so the foot of the screen never clips it.
 */
const TAB =
  'flex min-h-18 flex-col items-center justify-center gap-0.5 border-t-2 text-small ' +
  'focus-visible:outline-text-on-brand! focus-visible:-outline-offset-4!';
const HERE = 'border-text-on-brand-accent font-semibold text-text-on-brand';
const ELSEWHERE = 'border-transparent text-text-on-brand-muted hover:text-text-on-brand';

/**
 * A count's badge, at the icon's shoulder: the light gold of the bar's own rule with navy figures,
 * ringed in the bar's navy so it stands clear of the icon.
 */
const BADGE =
  'absolute -top-2 left-3.5 flex h-5 min-w-5 items-center justify-center rounded-full px-1 ' +
  'bg-text-on-brand-accent text-[0.8125rem] leading-none font-semibold text-surface-brand ' +
  'ring-2 ring-surface-brand';

/** Home's mark: a dot of the same gold, ringed the same, with no number, since it counts nothing. */
const DOT =
  'absolute -top-0.5 left-4 size-2.5 rounded-full bg-text-on-brand-accent ring-2 ring-surface-brand';

/** What a tab's mark draws and how the tab is then read, or null for nothing to mark. */
interface Marked {
  /** The badge's number, or null for a dot. */
  readonly badge: string | null;
  /** The name the tab is read by: its own, then what the mark says. */
  readonly name: string;
}

/** What the marks put on a tab, at the place shown. Messages counts nothing on Messages itself. */
function markOf(tab: Tab, marks: NavMarks, at: Tab | null): Marked | null {
  switch (tab) {
    case 'payments':
      return marks.toPay > 0
        ? { badge: countShown(marks.toPay), name: NAV_MARKS.toPay(countInWords(marks.toPay)) }
        : null;
    case 'messages':
      return marks.newMessages > 0 && at !== 'messages'
        ? {
            badge: countShown(marks.newMessages),
            name: NAV_MARKS.newMessages(countInWords(marks.newMessages)),
          }
        : null;
    case 'home':
      return marks.howWasIt ? { badge: null, name: NAV_MARKS.howWasIt } : null;
    case 'schedule':
      return null;
  }
}

interface BottomNavProps {
  /** The place shown, or null on a page that is none of the four, as Account is. */
  readonly at: Tab | null;
  /** Whether the person pays here, as the place shown says: only then is Payments a tab. */
  readonly payments?: boolean;
  /** What to mark on the tabs, as the place shown says (`lib/marks.ts`); nothing when absent. */
  readonly marks?: NavMarks;
}

export function BottomNav({ at, payments = false, marks }: BottomNavProps) {
  const shown = TABS.filter(({ tab }) => tab !== 'payments' || payments || at === 'payments');
  // Whole class names, so the build keeps both: a grid of four tabs, or of three.
  const columns = shown.length === 4 ? 'grid-cols-4' : 'grid-cols-3';
  return (
    <nav className="sticky bottom-0 bg-surface-brand pb-[env(safe-area-inset-bottom)]">
      <div className={`${COLUMN} grid ${columns} px-1`}>
        {shown.map(({ tab, to, icon }) => {
          const marked = marks === undefined ? null : markOf(tab, marks, at);
          return (
            <Link
              key={tab}
              to={to}
              aria-current={tab === at ? 'page' : undefined}
              aria-label={marked?.name}
              className={`${TAB} ${tab === at ? HERE : ELSEWHERE}`}
            >
              <span className="relative flex">
                {icon}
                {marked === null ? null : (
                  <span
                    data-mark
                    aria-hidden="true"
                    className={marked.badge === null ? DOT : BADGE}
                  >
                    {marked.badge}
                  </span>
                )}
              </span>
              <span>{NAV[tab]}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
